import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Plus, MessageSquare, ChevronDown, ChevronUp, History, Send, Pencil, X } from 'lucide-react';

// ============ GUIDELINES ============
export const Guidelines = () => {
  const { isAdmin, isReadOnly } = useAuth();
  const [guidelines, setGuidelines] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [comment, setComment] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newG, setNewG] = useState({ title: '', category: 'v2_message_sequence', vertical: 'all', content: '' });
  const [editingG, setEditingG] = useState(null);

  useEffect(() => {
    api.get('/guidelines').then(r => setGuidelines(r.data.guidelines || [])).catch(console.error);
  }, []);

  const toggleExpand = async (id) => {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    try {
      const res = await api.get(`/guidelines/${id}`);
      setDetail(res.data);
      setExpanded(id);
    } catch (err) { console.error(err); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/guidelines', { ...newG, content: newG.content });
      const res = await api.get('/guidelines');
      setGuidelines(res.data.guidelines || []);
      setShowAdd(false);
      setNewG({ title: '', category: 'v2_message_sequence', vertical: 'all', content: '' });
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingG) return;
    try {
      await api.put(`/guidelines/${editingG.id}`, {
        title: editingG.title,
        category: editingG.category,
        vertical: editingG.vertical,
        content: editingG.current_content
      });
      const res = await api.get('/guidelines');
      setGuidelines(res.data.guidelines || []);
      // Refresh the expanded detail if we were viewing this one
      if (expanded === editingG.id) {
        const detailRes = await api.get(`/guidelines/${editingG.id}`);
        setDetail(detailRes.data);
      }
      setEditingG(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Error updating guideline');
    }
  };

  const handleComment = async (guidelineId) => {
    if (!comment.trim()) return;
    try {
      await api.post(`/guidelines/${guidelineId}/comments`, { comment_text: comment });
      setComment('');
      toggleExpand(guidelineId);
    } catch (err) { alert('Error adding comment'); }
  };

  const categoryLabels = {
    v2_message_sequence: 'V2 Message Sequence', proof_points: 'Proof Points',
    hook_rules: 'Hook Rules', follow_up_sequence: 'Follow-Up Sequence',
    type_ab_filter: 'Type A/B Filter', cheat_sheet: 'Cheat Sheet', general: 'General',
    follow_up_prompt: 'Follow-up Prompt',
    ai_workspace_prompt_admin: 'AI Workspace Prompt — Admin',
    ai_workspace_prompt_bde: 'AI Workspace Prompt — BDE'
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h2>Message Guidelines</h2>
          <p>V2 sequences, proof points, and outreach rules</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
            <Plus size={16} /> Add Guideline
          </button>
        )}
      </div>

      {showAdd && isAdmin && (
        <div className="card">
          <form onSubmit={handleAdd}>
            <div className="form-row">
              <div className="form-group">
                <label>Title</label>
                <input value={newG.title} onChange={e => setNewG({...newG, title: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select value={newG.category} onChange={e => setNewG({...newG, category: e.target.value})}>
                  {Object.entries(categoryLabels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Vertical</label>
                <select value={newG.vertical} onChange={e => setNewG({...newG, vertical: e.target.value})}>
                  <option value="all">All Verticals</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="fintech">FinTech</option>
                  <option value="real_estate_proptech">Real Estate</option>
                  <option value="edtech">EdTech</option>
                  <option value="consulting_enterprise">Consulting</option>
                  <option value="saas_general">SaaS</option>
                  <option value="b2c">B2C</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Content</label>
              <textarea value={newG.content} onChange={e => setNewG({...newG, content: e.target.value})}
                rows={10} required placeholder="Write guideline content here..." />
            </div>
            <button type="submit" className="btn btn-primary">Publish Guideline</button>
          </form>
        </div>
      )}

      {guidelines.map(g => (
        <div className="card" key={g.id} style={{ cursor: 'pointer' }}>
          <div onClick={() => toggleExpand(g.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ fontSize: 15 }}>{g.title}</h3>
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <span className="badge badge-blue">{categoryLabels[g.category] || g.category}</span>
                <span className="badge badge-gray">{g.vertical === 'all' ? 'All Verticals' : g.vertical}</span>
                <span className="badge badge-gray">v{g.version}</span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  style={{ padding: '4px 8px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingG({
                      id: g.id,
                      title: g.title,
                      category: g.category,
                      vertical: g.vertical,
                      current_content: g.current_content
                    });
                  }}
                  title="Edit guideline"
                >
                  <Pencil size={14} />
                </button>
              )}
              {expanded === g.id ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          </div>

          {expanded === g.id && detail && (
            <div style={{ marginTop: 16, borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
              <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#374151', background: '#F9FAFB', padding: 16, borderRadius: 8 }}>
                {detail.guideline.current_content}
              </div>

              {detail.versions?.length > 1 && (
                <details style={{ marginTop: 16 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 13, color: '#6B7280' }}>
                    <History size={14} style={{ marginRight: 4 }} /> Version History ({detail.versions.length} versions)
                  </summary>
                  {detail.versions.map(v => (
                    <div key={v.id} style={{ marginTop: 8, padding: 12, background: '#F3F4F6', borderRadius: 6, fontSize: 13 }}>
                      <strong>v{v.version_number}</strong> by {v.changed_by_name} — {v.change_summary || 'No summary'}
                      <div style={{ color: '#9CA3AF', fontSize: 12 }}>{new Date(v.created_at).toLocaleString()}</div>
                    </div>
                  ))}
                </details>
              )}

              <div style={{ marginTop: 16 }}>
                <h4 style={{ fontSize: 13, color: '#6B7280', marginBottom: 8 }}>
                  <MessageSquare size={14} /> Comments & Suggestions ({detail.comments?.length || 0})
                </h4>
                {detail.comments?.map(c => (
                  <div key={c.id} style={{ padding: 12, background: c.status === 'approved' ? '#E8F5E9' : c.status === 'rejected' ? '#FFEBEE' : '#F3F4F6', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>
                    <strong>{c.commenter_name}</strong>: {c.comment_text}
                    {c.status !== 'pending' && (
                      <div style={{ marginTop: 4, color: c.status === 'approved' ? '#2E7D32' : '#C62828' }}>
                        {c.status.toUpperCase()} {c.admin_response && `— ${c.admin_response}`}
                      </div>
                    )}
                  </div>
                ))}
                {!isReadOnly && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input value={comment} onChange={e => setComment(e.target.value)}
                      placeholder="Suggest an edit or leave a comment..."
                      style={{ flex: 1, padding: '8px 12px', border: '1px solid #D1D5DB', borderRadius: 6, fontSize: 13 }} />
                    <button className="btn btn-sm btn-primary" onClick={() => handleComment(g.id)}>
                      <Send size={14} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
      {guidelines.length === 0 && <div className="empty-state"><p>No guidelines published yet.</p></div>}

      {editingG && (
        <div
          onClick={() => setEditingG(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, width: '100%', maxWidth: 800,
              maxHeight: '90vh', display: 'flex', flexDirection: 'column'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '16px 24px', borderBottom: '1px solid #e5e7eb' }}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Edit Guideline</h2>
              <button
                type="button"
                onClick={() => setEditingG(null)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleEdit} style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
              <div className="form-group">
                <label>Title</label>
                <input
                  value={editingG.title}
                  onChange={e => setEditingG({ ...editingG, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select
                  value={editingG.category}
                  onChange={e => setEditingG({ ...editingG, category: e.target.value })}
                >
                  {Object.entries(categoryLabels).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Vertical</label>
                <select
                  value={editingG.vertical}
                  onChange={e => setEditingG({ ...editingG, vertical: e.target.value })}
                >
                  <option value="all">All Verticals</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="fintech">FinTech</option>
                  <option value="real_estate_proptech">Real Estate / PropTech</option>
                  <option value="edtech">EdTech</option>
                  <option value="consulting_enterprise">Consulting / Enterprise</option>
                  <option value="saas_general">SaaS / General</option>
                  <option value="b2c">B2C</option>
                </select>
              </div>
              <div className="form-group">
                <label>Content</label>
                <textarea
                  value={editingG.current_content}
                  onChange={e => setEditingG({ ...editingG, current_content: e.target.value })}
                  rows={20}
                  style={{ fontFamily: 'monospace', fontSize: 12 }}
                  required
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button type="button" className="btn" onClick={() => setEditingG(null)}>Cancel</button>
                <button type="submit" className="btn btn-primary">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ REVIEW NOTES ============
export const ReviewNotes = () => {
  const { isAdminOrTL, user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ target_type: 'team', target_user_id: '', week_start: '', note_text: '' });

  useEffect(() => {
    api.get('/review-notes').then(r => setNotes(r.data.notes || [])).catch(console.error);
    if (isAdminOrTL) {
      api.get('/users').then(r => setUsers(r.data.users?.filter(u => u.role === 'bde') || [])).catch(() => {});
    }
  }, [isAdminOrTL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/review-notes', form);
      const res = await api.get('/review-notes');
      setNotes(res.data.notes || []);
      setShowAdd(false);
      setForm({ target_type: 'team', target_user_id: '', week_start: '', note_text: '' });
    } catch (err) { alert('Error creating note'); }
  };

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
        <div>
          <h2>Review Notes</h2>
          <p>Weekly feedback and strategy updates</p>
        </div>
        {isAdminOrTL && (
          <button className="btn btn-primary" onClick={() => setShowAdd(!showAdd)}>
            <Plus size={16} /> Add Note
          </button>
        )}
      </div>

      {showAdd && (
        <div className="card">
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Week Starting</label>
                <input type="date" value={form.week_start} onChange={e => setForm({...form, week_start: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Send To</label>
                <select value={form.target_type} onChange={e => setForm({...form, target_type: e.target.value, target_user_id: ''})}>
                  <option value="team">Entire Team</option>
                  <option value="individual">Individual BDE</option>
                </select>
              </div>
              {form.target_type === 'individual' && (
                <div className="form-group">
                  <label>Select BDE</label>
                  <select value={form.target_user_id} onChange={e => setForm({...form, target_user_id: e.target.value})} required>
                    <option value="">Select...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Note</label>
              <textarea value={form.note_text} onChange={e => setForm({...form, note_text: e.target.value})}
                rows={5} required placeholder="Write your review note here..." />
            </div>
            <button type="submit" className="btn btn-primary">Publish Note</button>
          </form>
        </div>
      )}

      {notes.map(n => (
        <div className="card" key={n.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
            <div>
              <strong>{n.author_name}</strong>
              <span className={`badge ${n.target_type === 'team' ? 'badge-blue' : 'badge-amber'}`} style={{ marginLeft: 8 }}>
                {n.target_type === 'team' ? 'Team' : 'Individual'}
              </span>
            </div>
            <span style={{ fontSize: 12, color: '#9CA3AF' }}>Week of {new Date(n.week_start).toLocaleDateString()}</span>
          </div>
          <p style={{ whiteSpace: 'pre-wrap', fontSize: 14 }}>{n.note_text}</p>
        </div>
      ))}
      {notes.length === 0 && <div className="empty-state"><p>No review notes yet.</p></div>}
    </div>
  );
};

// ============ PROFILE ASSIGNMENTS ============
export const ProfileAssignments = () => {
  const { isAdmin } = useAuth();
  const [assignments, setAssignments] = useState([]);

  useEffect(() => {
    api.get('/profiles').then(r => setAssignments(r.data.assignments || [])).catch(console.error);
  }, []);

  const grouped = assignments.reduce((acc, a) => {
    if (!acc[a.operator_name]) acc[a.operator_name] = [];
    acc[a.operator_name].push(a);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <h2>Profile Assignments</h2>
        <p>Which BDE operates which LinkedIn profile</p>
      </div>

      {Object.entries(grouped).map(([operator, profiles]) => (
        <div className="card" key={operator}>
          <h3 style={{ marginBottom: 12 }}>{operator}</h3>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr><th>Profile Name</th><th>Tier</th><th>Title</th><th>Target Segment</th></tr>
              </thead>
              <tbody>
                {profiles.map(p => (
                  <tr key={p.id}>
                    <td><strong>{p.profile_name}</strong></td>
                    <td><span className={`badge ${p.profile_tier === 'sales_nav' ? 'badge-blue' : p.profile_tier === 'premium' ? 'badge-amber' : 'badge-gray'}`}>
                      {p.profile_tier}
                    </span></td>
                    <td>{p.profile_title || '-'}</td>
                    <td>{p.target_segment || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {assignments.length === 0 && <div className="empty-state"><p>No profile assignments configured.</p></div>}
    </div>
  );
};
